import { Link } from "react-router-dom";
import { Button, buttonClass } from "@a2z/ui";
import { keys } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";

type RouteErrorFallbackProps = {
  /** Re-render the failed subtree (Sentry's `resetError`). */
  onRetry: () => void;
  /** Where the "go to dashboard" escape hatch points. */
  homeHref: string;
  /**
   * Fill the viewport and carry its own card chrome. Used when the throw took
   * the shell down with it (see `RouteErrorElement`) — there is no content
   * pane left to sit inside, so the fallback has to be the whole screen.
   */
  fullScreen?: boolean;
  /**
   * Raw error text, collapsed. Callers pass this only under
   * `import.meta.env.DEV` — a seller must never be shown a stack trace.
   */
  debugDetail?: string;
};

/**
 * Fallback shown when a route throws. By default it fills the content area
 * rather than the viewport, so the surrounding shell (sidebar/topbar) stays
 * usable; `fullScreen` is for the case where the shell itself is gone.
 * Direction/locale are inherited from the `.seller-app` wrapper, so no `dir`
 * handling is needed here.
 */
export function RouteErrorFallback({
  onRetry,
  homeHref,
  fullScreen = false,
  debugDetail,
}: RouteErrorFallbackProps) {
  const t = useTranslations();

  return (
    <div
      role="alert"
      className={`flex w-full flex-col items-center justify-center gap-5 p-6 text-center ${
        fullScreen ? "min-h-[100dvh]" : "min-h-[60vh]"
      }`}
    >
      <div
        className={
          fullScreen
            ? "flex w-full max-w-md flex-col items-center gap-5 rounded-[var(--radius-card,16px)] border-[length:var(--border-card-width,1px)] border-[color:var(--border-light)] bg-[color:var(--surface)] px-6 py-8"
            : "flex w-full max-w-md flex-col items-center gap-5"
        }
      >
        <div>
          <h1 className="text-base font-semibold text-[color:var(--text-primary)]">
            {t(keys.errors.crashTitle)}
          </h1>
          <p className="mt-2 text-sm text-[color:var(--text-muted)]">
            {t(keys.errors.recovery)}
          </p>
        </div>
        {/* Stacked by default: side by side, the two Arabic labels leave no
            comfortable tap target on a 360px phone. */}
        <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
          <Button type="button" variant="primary" size="md" onClick={onRetry}>
            {t(keys.common.retry)}
          </Button>
          <Link
            to={homeHref}
            className={buttonClass({ variant: "outline", size: "md" })}
          >
            {t(keys.errors.backToDashboard)}
          </Link>
        </div>
      </div>
      {debugDetail ? (
        <details className="w-full max-w-md text-start">
          <summary className="cursor-pointer text-xs text-[color:var(--text-muted)]">
            {t(keys.errors.debugDetails)}
          </summary>
          <pre
            dir="ltr"
            className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-card,16px)] bg-[color:var(--surface-alt)] p-3 text-start text-[11px] text-[color:var(--text-muted)]"
          >
            {debugDetail}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
